import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useMatrixClient } from '../../context/MatrixContext';
import { useI18n } from '../../context/I18nContext';
import { useToast } from '../../context/ToastContext';
import logger from '../../utils/logger';
import packageJson from '../../../package.json';
import { getOSVersionString, getUserAgent, getBrowserVersionString } from '../../utils/deviceInfo';
import { isMockAuthenticatorEnabled, setMockAuthenticatorEnabled } from '../../utils/devToggles';
import SettingsToggle from './SettingsToggle';
import './DeveloperInfoPage.css';

const DeveloperInfoPage = ({ onBack }) => {
  const client = useMatrixClient();
  const [buildInfo, setBuildInfo] = useState(null);
  const [mockAuth, setMockAuth] = useState(isMockAuthenticatorEnabled);
  const { t } = useI18n();
  const { showToast, showError } = useToast();

  // Fetch build info from meta.json
  useEffect(() => {
    // Create AbortController to cancel fetch on unmount
    const abortController = new AbortController();

    const fetchBuildInfo = async () => {
      try {
        const response = await fetch('/meta.json', {
          signal: abortController.signal,
        });
        if (response.ok) {
          const data = await response.json();
          setBuildInfo(data);
        }
      } catch (error) {
        // Ignore AbortError (expected when component unmounts)
        if (error.name !== 'AbortError') {
          logger.debug('Could not fetch build info:', error);
        }
      }
    };
    fetchBuildInfo();

    // Cleanup: abort fetch on unmount
    return () => {
      abortController.abort();
    };
  }, []);

  const formatBuildTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Get Matrix homeserver URL
  const getHomeserverUrl = () => {
    if (!client) return 'Not connected';
    try {
      return client.baseUrl || client.getHomeserverUrl() || 'Unknown';
    } catch {
      return 'Unknown';
    }
  };

  // Get Matrix user ID
  const getUserId = () => {
    if (!client) return 'Not connected';
    try {
      return client.getUserId() || 'Unknown';
    } catch {
      return 'Unknown';
    }
  };

  const osVersion = getOSVersionString();
  const userAgent = getUserAgent();
  const browserVersion = getBrowserVersionString();
  const homeserverUrl = getHomeserverUrl();
  const userId = getUserId();
  const buildVersion = buildInfo?.commitId;
  const buildDate = buildInfo?.buildTime ? formatBuildTime(buildInfo.buildTime) : null;

  const developerInfoSections = useMemo(() => {
    const appInfoItems = [
      {
        label: t('SessionsTableView.appVer', {}, 'App Version'),
        value: `v${packageJson.version}`,
      },
    ];

    if (buildVersion) {
      appInfoItems.push({
        label: t('DeveloperInfoPage.buildVersion', {}, 'Build Version'),
        value: buildVersion,
      });
    }

    if (buildDate) {
      appInfoItems.push({
        label: t('DeveloperInfoPage.buildDate', {}, 'Build Date'),
        value: buildDate,
      });
    }

    const sections = [
      {
        key: 'app',
        title: t('DeveloperView.appInfo', {}, 'App Info'),
        items: appInfoItems,
      },
      {
        key: 'device',
        title: t('DeveloperView.deviceInfo', {}, 'Device Info'),
        items: [
          {
            label: t('DeveloperView.osVersion', {}, 'OS Version'),
            value: osVersion,
          },
          {
            label: t('DeveloperView.browserVersion', {}, 'Browser Version'),
            value: browserVersion,
          },
          {
            label: t('DeveloperView.userAgent', {}, 'User Agent'),
            value: userAgent,
          },
        ],
      },
      {
        key: 'account',
        title: t('DeveloperView.accountInfo', {}, 'Account Info'),
        items: [
          {
            label: t('DeveloperInfoPage.matrixHomeserver', {}, 'Matrix Homeserver'),
            value: homeserverUrl,
          },
          {
            label: t('DeveloperInfoPage.matrixUserId', {}, 'User ID'),
            value: userId,
          },
        ],
      },
    ];

    return sections;
  }, [buildDate, buildVersion, browserVersion, homeserverUrl, osVersion, t, userAgent, userId]);

  const developerInfoText = useMemo(() => {
    const sectionsText = developerInfoSections
      .map((section) => {
        const lines = [
          section.title,
          ...section.items.map((item) => `${item.label}: ${item.value ?? 'N/A'}`),
        ];
        return lines.join('\n');
      })
      .join('\n\n');
    return `${sectionsText}\n\nDeveloper Settings\nMock Authenticator: ${mockAuth ? 'enabled' : 'disabled'}`;
  }, [developerInfoSections, mockAuth]);

  const fallbackCopyToClipboard = (text) => {
    if (typeof document === 'undefined') {
      logger.warn('Document is not available for fallback clipboard copy');
      return false;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.opacity = '0';
    // snyk:ignore:DOM-based Cross-site Scripting (XSS) -- Text is generated from trusted app/device info and localized labels
    document.body.appendChild(textArea);
    textArea.select();

    try {
      const successful = document.execCommand && document.execCommand('copy');
      if (!successful) {
        throw new Error('execCommand copy returned false');
      }
      return true;
    } catch (error) {
      logger.error('Developer info fallback copy failed', error);
      return false;
    } finally {
      document.body.removeChild(textArea);
    }
  };

  const copyDeveloperInfo = useCallback(async () => {
    if (!developerInfoText) return false;

    if (typeof navigator !== 'undefined' && navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(developerInfoText);
        return true;
      } catch (error) {
        logger.warn('navigator.clipboard write failed, falling back to execCommand', error);
      }
    }

    return fallbackCopyToClipboard(developerInfoText);
  }, [developerInfoText]);

  const handleCopyClick = useCallback(async () => {
    const success = await copyDeveloperInfo();
    if (success) {
      showToast({
        type: 'success',
        title: t('SettingsView.developerInfo', {}, 'Developer Info'),
        message: t('SettingsView.developerInfoCopyToClipboardDesc', {}, 'Developer Info has been copied to clipboard'),
        countdown: 3,
        position: 'top-left',
      });
    } else {
      showError(
        t('SettingsView.developerInfo', {}, 'Developer Info'),
        t('DeveloperInfoPage.copyFailure', {}, 'Unable to copy Developer Info. Please try again.'),
      );
    }
  }, [copyDeveloperInfo, showError, showToast, t]);

  return (
    <div className="developer-info-page">
      <div className="developer-info-header">
        <button
          className="developer-info-header-button"
          onClick={onBack}
          aria-label={t('SignInForm.goBack', {}, 'Go Back')}
          title={t('SignInForm.goBack', {}, 'Go Back')}
        >
          <ion-icon name="chevron-back-outline" />
        </button>
        <span className="developer-info-header-title">
          {t('SettingsView.developerInfo', {}, 'Developer Info')}
        </span>
        <button
          className="developer-info-header-button"
          onClick={handleCopyClick}
          aria-label={t('SettingsView.developerInfoCopyToClipboardDesc', {}, 'Copy developer info')}
          title={t('SettingsView.developerInfoCopyToClipboardDesc', {}, 'Copy developer info')}
        >
          <ion-icon name="clipboard-outline" />
        </button>
      </div>
      <div className="developer-info-content">
        {developerInfoSections.map((section) => (
          <div className="info-section" key={section.key}>
            <div className="info-section-header">{section.title}</div>
            {section.items.map((item, index) => (
              <div className="info-item" key={`${section.key}-${index}`}>
                <div className="info-label">{item.label}</div>
                <div className="info-value">{item.value}</div>
              </div>
            ))}
          </div>
        ))}
        <div className="info-section">
          <div className="info-section-header">
            {t('DeveloperInfoPage.developerSettings', {}, 'Developer Settings')}
          </div>
          <div className="info-section-content">
            <SettingsToggle
              icon="fa-flask"
              label={t('DeveloperInfoPage.mockAuthenticatorLabel', {}, 'Mock Authenticator')}
              description={t(
                'DeveloperInfoPage.mockAuthenticatorDescription',
                {},
                'Skip the system Authenticator app and use the homeserver mock-authenticator endpoint. Dev/test only.',
              )}
              checked={mockAuth}
              onChange={(value) => {
                setMockAuthenticatorEnabled(value);
                setMockAuth(value);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

DeveloperInfoPage.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default DeveloperInfoPage;
